import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createListening, getListeningById, updateListening } from '../../api/listeningApi';
import {
  Box,
  Typography,
  Grid,
  Stepper,
  Step,
  StepLabel,
  CircularProgress,
  Checkbox,
  FormControlLabel,
  Card,
  CardContent,
  Avatar,
  Paper,
  Chip,
} from '@mui/material';
import {
  Hearing as HearingIcon,
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  NavigateNext as NavigateNextIcon,
  NavigateBefore as NavigateBeforeIcon,
  Info as InfoIcon,
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  AudioFile as AudioFileIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import ModernButton from '../../components/common/ModernButton';
import ModernTextField from '../../components/common/ModernTextField';
import {
  parseIntervalToCount,
  validateIntervalSequence,
  calculateTotalQuestionCount,
  validateTotalQuestionCount,
} from '../../utils/intervalUtils';

const ListeningForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [listening, setListening] = useState({
    label: '',
    isPractice: false,
    voiceRecordUrl: '',
    part1: { part: 1, totalQuestionInterval: '', totalQuestionCount: 0, instructionLabel: '' },
    part2: { part: 2, totalQuestionInterval: '', totalQuestionCount: 0, instructionLabel: '' },
    part3: { part: 3, totalQuestionInterval: '', totalQuestionCount: 0, instructionLabel: '' },
    part4: { part: 4, totalQuestionInterval: '', totalQuestionCount: 0, instructionLabel: '' }
  });

  const partKeys = ['part1', 'part2', 'part3', 'part4'];
  const [activeStep, setActiveStep] = useState(0);
  const [errors, setErrors] = useState({ part1: {}, part2: {}, part3: {}, part4: {} });
  const [totalQuestionCountError, setTotalQuestionCountError] = useState('');
  const [intervalError, setIntervalError] = useState('');
  const [uploading, setUploading] = useState(false);


  useEffect(() => {
    if (id) {
      const fetchListening = async () => {
        try {
          const { data } = await getListeningById(id);
          setListening({
            ...data,
            isPractice: data.isPractice || false,
            part1: { part: 1, ...data.part1 },
            part2: { part: 2, ...data.part2 },
            part3: { part: 3, ...data.part3 },
            part4: { part: 4, ...data.part4 }
          });
        } catch (error) {
          console.error('Failed to fetch listening:', error);
        }
      };
      fetchListening();
    }
  }, [id]);

  useEffect(() => {
    const totalCount = calculateTotalQuestionCount(listening, partKeys);
    setTotalQuestionCountError(validateTotalQuestionCount(totalCount));
  }, [listening.part1.totalQuestionCount, listening.part2.totalQuestionCount, listening.part3.totalQuestionCount, listening.part4.totalQuestionCount]);

  useEffect(() => {
    const intervals = partKeys.map(key => listening[key].totalQuestionInterval);
    setIntervalError(validateIntervalSequence(intervals, 'Part'));
  }, [listening.part1.totalQuestionInterval, listening.part2.totalQuestionInterval, listening.part3.totalQuestionInterval, listening.part4.totalQuestionInterval]);

  const handleChange = (e, partKey) => {
    const { name, value, type, checked } = e.target;
    if (partKey) {
      if (name === 'totalQuestionInterval') {
        const calculatedCount = parseIntervalToCount(value);
        setListening((prev) => ({
          ...prev,
          [partKey]: { ...prev[partKey], totalQuestionInterval: value, totalQuestionCount: calculatedCount }
        }));
        setErrors((prev) => ({ ...prev, [partKey]: { ...prev[partKey], totalQuestionInterval: '' } }));
      } else {
        setListening((prev) => ({
          ...prev,
          [partKey]: { ...prev[partKey], [name]: value }
        }));
        setErrors((prev) => ({ ...prev, [partKey]: { ...prev[partKey], [name]: '' } }));
      }
    } else {
      setListening((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    }
  };

  const validatePart = (key) => {
    const errs = {};
    const data = listening[key];
    if (!data.instructionLabel.trim()) errs.instructionLabel = 'Instruction label is required';
    if (!data.totalQuestionInterval.trim()) errs.totalQuestionInterval = 'Question interval is required';
    return errs;
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'your_upload_preset');
    formData.append('folder', 'voice_recordings');
    try {
      const response = await axios.post('https://api.cloudinary.com/v1_1/your_cloud_name/upload', formData);
      setListening((prev) => ({ ...prev, voiceRecordUrl: response.data.secure_url }));
    } catch (err) {
      console.error('Upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const collectedErrors = {};
    partKeys.forEach((k) => { collectedErrors[k] = validatePart(k); });
    const hasErrors = totalQuestionCountError || intervalError || Object.values(collectedErrors).some((er) => Object.keys(er).length > 0);
    if (hasErrors) {
      setErrors(collectedErrors);
      showToast('Please fix validation errors before saving.', 'error');
      return;
    }
    try {
      if (id) {
        await updateListening(id, listening);
      } else {
        await createListening(listening);
      }
      showToast('Listening saved successfully!', 'success');
      navigate('/listenings');
    } catch (error) {
      console.error('Failed to save listening:', error);
      showToast('Failed to save listening', 'error');
    }
  };

  return (
    <Box>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar
            sx={{
              backgroundColor: 'primary.main',
              width: 48,
              height: 48,
              mr: 2,
            }}
          >
            <HearingIcon />
          </Avatar>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 600, color: 'text.primary' }}>
              {id ? 'Edit Listening' : 'Create Listening'}
            </Typography>
            <Typography variant="body1" sx={{ color: 'text.secondary' }}>
              {id ? 'Update listening content and parts' : 'Set up a new listening with multiple parts and audio recording'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={3}>
          {/* Basic Information Card */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'info.main', width: 32, height: 32, mr: 2 }}>
                    <InfoIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Basic Information
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={8}>
                    <ModernTextField
                      label="Listening Title"
                      name="label"
                      value={listening.label}
                      onChange={handleChange}
                      fullWidth
                      required
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      <FormControlLabel
                        control={
                          <Checkbox 
                            name="isPractice" 
                            checked={listening.isPractice}
                            sx={{
                              '&.Mui-checked': {
                                color: 'primary.main',
                              },
                            }}
                          />
                        }
                        label={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body1" sx={{ fontWeight: 500 }}>
                              Practice Test
                            </Typography>
                            <Chip 
                              label={listening.isPractice ? 'Yes' : 'No'} 
                              color={listening.isPractice ? 'info' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                        }
                        onChange={(e) => handleChange(e)}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Audio Upload Card */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'warning.main', width: 32, height: 32, mr: 2 }}>
                    <CloudUploadIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Audio Recording
                  </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <input 
                    type="file" 
                    accept="audio/*" 
                    onChange={handleFileUpload} 
                    style={{ display: 'none' }}
                    id="audio-upload"
                  />
                  <label htmlFor="audio-upload">
                    <ModernButton
                      component="span"
                      variant="outlined"
                      startIcon={<CloudUploadIcon />}
                      disabled={uploading}
                    >
                      {uploading ? 'Uploading...' : 'Upload Audio File'}
                    </ModernButton>
                  </label>
                  {uploading && <CircularProgress size={24} />}
                </Box>

                {listening.voiceRecordUrl && (
                  <Paper sx={{ p: 2, backgroundColor: 'success.50', borderRadius: 2, border: '1px solid', borderColor: 'success.main' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <AudioFileIcon sx={{ color: 'success.main' }} />
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: 'success.main' }}>
                          Audio file uploaded successfully
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                          <a href={listening.voiceRecordUrl} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>
                            View Recording
                          </a>
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Progress Stepper */}
          <Grid item xs={12}>
            <Card sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'success.main', width: 32, height: 32, mr: 2 }}>
                    <CheckCircleIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Parts Progress
                  </Typography>
                </Box>
                
                <Stepper 
                  activeStep={activeStep} 
                  sx={{ 
                    '& .MuiStepLabel-root': {
                      '& .MuiStepLabel-label': {
                        fontWeight: 600,
                        fontSize: '1rem',
                      },
                    },
                  }}
                >
                  {partKeys.map((key, idx) => (
                    <Step key={key}>
                      <StepLabel>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>Part {idx + 1}</Typography>
                          {listening[key]?.totalQuestionCount > 0 && (
                            <Chip 
                              label={`${listening[key].totalQuestionCount} questions`}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          )}
                        </Box>
                      </StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
        {/* Current Part Content */}
        {(() => {
          const partKey = partKeys[activeStep];
          return (
            <Card key={partKey} sx={{ borderRadius: 3, mb: 3 }}>
              <CardContent sx={{ p: 4 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Avatar sx={{ backgroundColor: 'warning.main', width: 32, height: 32, mr: 2 }}>
                    <HearingIcon />
                  </Avatar>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Part {activeStep + 1} Configuration
                  </Typography>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Instruction Label"
                      name="instructionLabel"
                      value={listening[partKey].instructionLabel}
                      onChange={(e) => handleChange(e, partKey)}
                      fullWidth
                      required
                      error={Boolean(errors[partKey].instructionLabel)}
                      helperText={errors[partKey].instructionLabel}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Part Number
                      </Typography>
                      <Chip 
                        label={`Part ${activeStep + 1}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontWeight: 500 }}
                      />
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} md={6}>
                    <ModernTextField
                      label="Question Interval (e.g., 1-10)"
                      name="totalQuestionInterval"
                      value={listening[partKey].totalQuestionInterval}
                      onChange={(e) => handleChange(e, partKey)}
                      fullWidth
                      required
                      error={Boolean(errors[partKey].totalQuestionInterval)}
                      helperText={errors[partKey].totalQuestionInterval}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box sx={{ p: 2, backgroundColor: 'grey.50', borderRadius: 2, textAlign: 'center' }}>
                      <Typography variant="subtitle2" sx={{ color: 'text.secondary', mb: 1 }}>
                        Question Count (Auto-calculated)
                      </Typography>
                      <Typography variant="h6" sx={{ color: 'primary.main', fontWeight: 600 }}>
                        {listening[partKey].totalQuestionCount || 0} questions
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          );
        })()}

        {/* Error Messages */}
        {(totalQuestionCountError || intervalError) && (
          <Card sx={{ borderRadius: 3, mb: 3, border: '1px solid', borderColor: 'error.main' }}>
            <CardContent sx={{ p: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Avatar sx={{ backgroundColor: 'error.main', width: 32, height: 32, mr: 2 }}>
                  <InfoIcon />
                </Avatar>
                <Typography variant="h6" sx={{ fontWeight: 600, color: 'error.main' }}>
                  Validation Errors
                </Typography>
              </Box>
              {totalQuestionCountError && (
                <Typography color="error" sx={{ mb: 1 }}>
                  {totalQuestionCountError}
                </Typography>
              )}
              {intervalError && (
                <Typography color="error">
                  {intervalError}
                </Typography>
              )}
            </CardContent>
          </Card>
        )}

        {/* Navigation Buttons */}
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <ModernButton
                disabled={activeStep === 0}
                variant="outlined"
                startIcon={<NavigateBeforeIcon />}
                onClick={() => setActiveStep((s) => s - 1)}
              >
                Previous Part
              </ModernButton>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <ModernButton
                  onClick={() => navigate('/listenings')}
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                >
                  Back to List
                </ModernButton>
                
                {activeStep < partKeys.length - 1 ? (
                  <ModernButton
                    variant="contained"
                    endIcon={<NavigateNextIcon />}
                    onClick={() => {
                      const key = partKeys[activeStep];
                      const errs = validatePart(key);
                      if (Object.keys(errs).length > 0) {
                        setErrors((prev) => ({ ...prev, [key]: errs }));
                      } else {
                        setErrors((prev) => ({ ...prev, [key]: {} }));
                        setActiveStep((s) => s + 1);
                      }
                    }}
                  >
                    Next Part
                  </ModernButton>
                ) : (
                  <ModernButton 
                    type="submit" 
                    variant="contained" 
                    startIcon={<SaveIcon />}
                    disabled={!!totalQuestionCountError || !!intervalError}
                  >
                    Save Listening
                  </ModernButton>
                )}
              </Box>
            </Box>
          </CardContent>
        </Card>
      </form>
    </Box>
  );
};

export default ListeningForm;
